import dayjs, { Dayjs } from 'dayjs'
import { t } from 'i18next'
import { Chart } from 'primereact/chart'
import React, { useEffect, useState } from 'react'
import { ChartData, LineChartData, LineChartDataSet } from './Statistic.module'
import { API_OBSERVATIONS } from '../../../../constants'
import { RequestMethod } from '../../../../model/enums/enums'
import { Observation } from '../../../../model/observation'
import { useAppDispatch, useAppSelector } from '../../../../redux/hooks'

import {
  observationsCleared,
  observationsSet,
} from '../../../../redux/official-document/education-report/observation/observationReducer'
import { handleRequest } from '../../../../services/api/base/handleRequest'

export const ObservationStatistic = () => {
  const [totalChartData, setTotalChartData] = useState<ChartData | undefined>(
    undefined
  )
  const [currentMonthChartData, setCurrentMonthTotalChartData] = useState<
    ChartData | undefined
  >(undefined)

  const [lineChartData, setLineChartData] = useState<
    LineChartData | undefined
  >()

  const [observationsOfCurrentMonth, setObservationsOfCurrentMonth] = useState<
    Observation[]
  >([])

  const { loadedBasicInformation } = useAppSelector((state) => state.user)

  const observations = useAppSelector((state) => state.observations)
  const dispatcher = useAppDispatch()

  useEffect(() => {
    const loadObservations = async () => {
      if (!loadedBasicInformation) {
        return
      }
      if (observations.length === 0) {
        const result = await handleRequest<Observation[]>(
          API_OBSERVATIONS(),
          RequestMethod.GET
        )
        if (result.data && !result.error) {
          dispatcher(observationsCleared()) // making clear that there are no duplicates
          dispatcher(observationsSet(result.data))
        }
      }
    }
    loadObservations()
  }, [loadedBasicInformation])

  const getDataSet = (obs: Observation[]) => {
    const aCount = obs.filter((o) => o.rating === 1)?.length
    const bCount = obs.filter((o) => o.rating === 2)?.length
    const cCount = obs.filter((o) => o.rating === 3)?.length
    const dCount = obs.filter((o) => o.rating === 4)?.length

    return {
      labels: ['A', 'B', 'C', 'D'],
      datasets: [
        {
          data: [aCount, bCount, cCount, dCount],
          backgroundColor: ['#3F876CFF', '#7E9A6DFF', '#DA9A27FF', '#B73328FF'],
          hoverBackgroundColor: ['#59bb96', '#add396', '#ffb42f', '#ee4334'],
        },
      ],
    }
  }

  const dateIsFromCurrentMonth = (currentDate: Dayjs, dateToCheck: Dayjs) => {
    const year = currentDate.year()
    const month = currentDate.month()
    return dateToCheck.year() === year && dateToCheck.month() === month
  }

  const dateHasYearAndMonth = (
    year: number,
    monthIndex: number,
    date: Dayjs
  ) => {
    return year === date.year() && date.month() === monthIndex
  }

  const getObservationsByMonthYearAndRatingCount = (
    obs: Observation[],
    monthIndex: number,
    year: number,
    rating: number
  ): number => {
    const observationsByMonthAndYearAndRating = obs.filter(
      (o) =>
        dateHasYearAndMonth(year, monthIndex, dayjs(o.created_on)) &&
        o.rating === rating
    )

    return observationsByMonthAndYearAndRating?.length
  }

  const setupLineChartData = (allObservations: Observation[]) => {
    const data: LineChartData = {
      labels: [],
      datasets: [],
    }

    if (allObservations.length === 0) {
      return data
    }
    const x = allObservations?.sort(function (a, b) {
      if (!a.created_on || !b.created_on) return -1
      return (
        new Date(b.created_on).getTime() - new Date(a.created_on).getTime()
      )
    })

    // this is simple and could be error-prone but since we increment
    // and get them back in correct order we can give it a try
    // we get them in reverse order by date
    const first = x[x.length - 1]?.created_on
    const last = x[0].created_on
    if (first === last) {
      return data
    }

    const firstDate = dayjs(first)
    const lastDate = dayjs(last)
    const firstMonth = firstDate.month()
    const firstYear = firstDate.year()

    if (!lastDate || !firstDate) {
      return data
    }

    const totalMonths = lastDate.diff(firstDate, 'months', true)

    const labels: string[] = []
    const dSets: LineChartDataSet[] = []

    let monthIndex = firstMonth

    let currentYear = firstYear

    const aNumberArray: number[] = []
    const bNumberArray: number[] = []
    const cNumberArray: number[] = []
    const dNumberArray: number[] = []

    for (let i = 0; i < totalMonths + 1; i++) {
      const currentMonthName = t<string>(`locales.monthNames.${monthIndex}`)

      let labelString = `${currentMonthName} ${currentYear}`

      labels.push(labelString)

      aNumberArray.push(
        getObservationsByMonthYearAndRatingCount(x, monthIndex, currentYear, 1)
      )
      bNumberArray.push(
        getObservationsByMonthYearAndRatingCount(x, monthIndex, currentYear, 2)
      )
      cNumberArray.push(
        getObservationsByMonthYearAndRatingCount(x, monthIndex, currentYear, 3)
      )
      dNumberArray.push(
        getObservationsByMonthYearAndRatingCount(x, monthIndex, currentYear, 4)
      )

      // month index of december is 11(last month of year), we need to go to january (index 0) and increment the year
      if (monthIndex === 11) {
        monthIndex = 0
        currentYear++
      } else {
        monthIndex++
      }
    }

    dSets.push({
      data: aNumberArray,
      label: 'A',
      fill: false,
      borderColor: '#3F876CFF',
      tension: 0.4,
    })
    dSets.push({
      data: bNumberArray,
      label: 'B',
      fill: false,
      borderColor: '#7E9A6DFF',
      tension: 0.4,
    })
    dSets.push({
      data: cNumberArray,
      label: 'C',
      fill: false,
      borderColor: '#DA9A27FF',
      tension: 0.4,
    })
    dSets.push({
      data: dNumberArray,
      label: 'D',
      fill: true,
      borderColor: '#B73328FF',
      tension: 0.4,
    })

    data.labels = labels
    data.datasets = dSets

    setLineChartData(data)
  }

  useEffect(() => {
    if (!observations.length) {
      return
    }
    const totalObservations = getDataSet(observations)
    setTotalChartData(totalObservations)
    setupLineChartData([...observations])
    const now = dayjs()

    const observationsFromCurrentMonth = observations.filter((o: Observation) =>
      dateIsFromCurrentMonth(now, dayjs(o.created_on))
    )

    setObservationsOfCurrentMonth(observationsFromCurrentMonth)

    const currentMonthObservations = getDataSet(observationsFromCurrentMonth)
    setCurrentMonthTotalChartData(currentMonthObservations)
  }, [observations])

  return (
    <div className={'grid flex-wrap gap-2'}>
      <h3 className={'col-12'}>
        <b>{t<string>('navigation.observation')}</b>
      </h3>
      <div className={'col'}>
        <div
          className={
            'grid justify-content-center align-items-center p-2 card-hover'
          }
        >
          <h4 className={'statistic-text'}>
            {t<string>('core.statistic.count_observations_in_system', {
              total: observations.length,
            })}
          </h4>
          <Chart height={'200px'} type={'doughnut'} data={totalChartData} />
        </div>
        <div
          className={
            'grid justify-content-center align-items-center mt-6 p-2 card-hover'
          }
        >
          <h4 className={'statistic-text'}>
            {t<string>('core.statistic.count_observations_current_month', {
              total: observationsOfCurrentMonth.length,
            })}
          </h4>
          <Chart
            height={'200px'}
            type={'doughnut'}
            data={currentMonthChartData}
          />
        </div>
      </div>

      <div className={'col-8 justify-content-center p-2 card-hover'}>
        <h4 className={'statistic-text'}>
          {t<string>('core.statistic.observations_by_date')}
        </h4>
        <Chart
          type={'line'}
          height={'600px'}
          data={lineChartData}
          options={{
            maintainAspectRatio: false,
            aspectRatio: 1,
            plugins: {
              legend: {
                labels: {
                  color: '#495057',
                },
              },
            },
            scales: {
              x: {
                ticks: {
                  color: '#495057',
                },
                grid: {
                  color: '#ebedef',
                },
              },
              y: {
                ticks: {
                  color: '#495057',
                },
                grid: {
                  color: '#ebedef',
                },
              },
            },
          }}
        />
      </div>
    </div>
  )
}